/**
 * The pin: this operator's shortlist over the project register.
 *
 * A sibling of `projects.ts` rather than three more exports inside it, on the
 * same grounds as `projectPortfolio.ts`, `milestoneBoard.ts` and
 * `projectActivity.ts` before it - that file is past eight hundred lines and
 * already describes the register, the summary, the milestones, the time entries,
 * the expenses and the contracts (AGENTS.md §4).
 *
 * ── WHAT A PIN IS, AND WHAT IT IS NOT ──────────────────────────────────────
 *
 * It belongs to the PERSON, not to the project. `ProjectPin.ownerId` is the
 * signed-in operator, taken from the session on the server and never sent from
 * here, so two colleagues looking at the same register get two different
 * answers and neither can pin anything on the other's behalf. That is the whole
 * reason it is not a column on the project: a shared register with a shared pin
 * means the first person to use the feature reorders everybody else's screen.
 *
 * Nothing about the project changes when it is pinned. No report, no client and
 * no other operator can tell a pinned project from an unpinned one.
 *
 * ── WHY THE STRIP IS ITS OWN REQUEST ───────────────────────────────────────
 *
 * `getPinnedProjects` does not read the register's page. It cannot: the register
 * is filtered, sorted and paged, so a shortlist derived from the rows on screen
 * would lose a pinned project the moment the operator typed in the search box or
 * turned to page two - and lose it silently, which is the failure that makes a
 * shortcut untrustworthy. The strip asks its own question and gets the same
 * answer under every filter.
 */
import { api } from "./api.js";
import type { Project } from "./projects.js";

/** A pinned project: the register's own row, plus when it was pinned. */
export interface PinnedProject extends Project {
  /** Always true on this list. Present so a row can be passed straight to the
   *  menus, which read `project.pinned` to decide which direction to offer. */
  pinned: true;
  /** Orders the strip, newest first. Not shown - what it is for is the ORDER,
   *  and a date beside a project name would read as a deadline. */
  pinnedAt: string;
}

export interface PinnedProjects {
  rows: PinnedProject[];
  /**
   * Pins whose project is in the archive.
   *
   * The strip leaves those rows out - it is a shortcut to work in progress, and
   * archiving is the act of saying a project is not that any more. But dropping
   * them in silence would mean an operator who pinned six and archived two sees
   * four with nothing on screen admitting the other two are still bookmarked.
   * The pin row itself is untouched, so bringing a project back out of the
   * archive puts it back in the strip.
   */
  archivedCount: number;
}

/** What both writes answer with. `pinned` is the state AFTER the call, so a
 *  caller can set its local flag from the response rather than assuming the
 *  request did what it asked. */
export interface PinResult {
  ok: boolean;
  pinned: boolean;
  pinnedAt?: string;
}

/**
 * The operator's own pins.
 *
 * A static path under `/projects/`, which the router prefers over `/:id` - so
 * this is not read as a project whose id happens to be "pinned".
 */
export const getPinnedProjects = () => api.get<PinnedProjects>("/admin/crm/projects/pinned");

/**
 * "A pin moved" - broadcast to whatever else is drawing the shortlist.
 *
 * There are now two places that draw it: the strip over the register, which is
 * mounted beside the menus that write pins and can simply be told, and the
 * sidebar, which is mounted on EVERY screen and has no relationship to any of
 * them. Threading a callback from the project page, the row menu and the strip
 * up through the layout to the rail would be three props whose only job is to
 * carry the same sentence.
 *
 * A window event instead, dispatched by the two writes below so no caller has to
 * remember it. Listeners re-ASK rather than being handed the change: the whole
 * content of both drawings is one server answer, and the honest response to "a
 * pin moved somewhere" is to fetch it again.
 *
 * Same-tab only. A pin set in another tab is a shortlist this one learns about
 * on its next load, which is the same deal every other list in the CRM offers.
 *
 * ── WHY IT CARRIES WHICH PROJECT, WHEN NOBODY HAS TO READ IT ──────────────
 *
 * The two shortlists ignore the payload and re-ask, as above. The screens that
 * merely DRAW a pin cannot: the register holds twenty-five rows and the project
 * page holds one record, each with a `pinned` flag of its own, and neither can
 * refetch to move a badge - the register would put the whole list behind a
 * spinner for a bookmark, and the page would throw away the milestones it is
 * holding. So the event names the project and its new state, and those two
 * patch the flag where they stand.
 *
 * It matters because the pin is now writable from the SIDEBAR, which is on
 * screen on both of those pages. Before that, every write came from a menu
 * inside the screen that owned the row, and telling it was a local call.
 */
export const PINS_CHANGED = "crm:pins-changed";

/** What moved, and where it landed. `pinned` is the state AFTER the write. */
export interface PinChange {
  id: string;
  pinned: boolean;
}

/**
 * Listen for pin writes made anywhere in this tab.
 *
 * Returns its own unsubscribe, so a caller is one line: `useEffect(() =>
 * onPinsChanged(fn), [fn])`. The cast lives here rather than in four screens,
 * and so does the guard - a `PINS_CHANGED` without a detail is not this
 * module's event and is ignored rather than crashed on.
 */
export function onPinsChanged(handler: (change: PinChange) => void): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<PinChange>).detail;
    if (detail) handler(detail);
  };
  window.addEventListener(PINS_CHANGED, listener);
  return () => window.removeEventListener(PINS_CHANGED, listener);
}

function announcePins(id: string, pinned: boolean): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<PinChange>(PINS_CHANGED, { detail: { id, pinned } }));
  }
}

/**
 * Pin it, or leave it pinned.
 *
 * Idempotent on the server: a second call returns the existing pin with its
 * original date rather than moving it to the front of the strip. The one
 * refusal is `409 { error: "too_many_pins", limit }` - a shortlist has a ceiling
 * because it is drawn in full with no pager, and a wall of fifty projects above
 * the register is not a shortcut.
 */
export const pinProject = async (id: string): Promise<PinResult> => {
  const res = await api.post<PinResult>(`/admin/crm/projects/${id}/pin`);
  announcePins(id, res.pinned);
  return res;
};

/**
 * Unpin it, or leave it unpinned.
 *
 * DELETE on the same path, because what is deleted is the pin and not the
 * project. Never fails on a project that was not pinned: that is the state the
 * caller asked for, and a second click must not produce an error toast.
 */
export const unpinProject = async (id: string): Promise<PinResult> => {
  const res = await api.del<PinResult>(`/admin/crm/projects/${id}/pin`);
  announcePins(id, res.pinned);
  return res;
};
